import { useCallback, useEffect, useRef, useState } from 'react';
import { isSttSupported, startStt, SttHandle } from '../services/sttService';

export interface UseVoiceDictationOptions {
  /** Język rozpoznawania (domyślnie `pl-PL`). */
  lang?: string;
  /** Aktualna wartość pola tekstowego (kontrolowany input). */
  value: string;
  /** Setter pola tekstowego — wołany na każdy partial i final. */
  onChangeValue: (v: string) => void;
  /** Jeśli `true`, przycisk/hook są wyłączone (np. w trakcie wysyłania). */
  disabled?: boolean;
  /** Klucz w localStorage dla wybranego urządzenia audio (`MicrophoneSettingsDialog`). Domyślnie `stt:deviceId`. */
  deviceIdStorageKey?: string;
}

export interface UseVoiceDictationResult {
  isSupported: boolean;
  isRecording: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

/**
 * Hook dodający dyktowanie głosem (Web Speech API) do dowolnego pola tekstowego.
 *
 * Model użycia:
 *  - na `start()`: zapamiętuje aktualny `value` jako **baseline**,
 *  - na każdy **partial** i **final**: aktualizuje pole na `baseline + [...finals] + [currentPartial]`,
 *  - na `stop()`: utrwala `baseline + [...finals]` (partial znika, finale zostają),
 *  - jeśli user w trakcie nagrywania sam coś dopisze/zmodyfikuje — jego zmiana zostanie **nadpisana** przy najbliższym partialu (ograniczenie prostoty); w praktyce zatrzymaj nagrywanie przed ręczną edycją.
 *
 * Reużywa `services/sttService.ts` (tę samą implementację co strona transkrypcji).
 */
export function useVoiceDictation(opts: UseVoiceDictationOptions): UseVoiceDictationResult {
  const { lang = 'pl-PL', value, onChangeValue, disabled, deviceIdStorageKey = 'stt:deviceId' } = opts;

  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sttRef = useRef<SttHandle | null>(null);
  const baselineRef = useRef<string>('');
  const finalsRef = useRef<string>('');
  const onChangeRef = useRef(onChangeValue);
  const valueRef = useRef(value);

  useEffect(() => { onChangeRef.current = onChangeValue; }, [onChangeValue]);
  useEffect(() => { valueRef.current = value; }, [value]);

  const isSupported = isSttSupported();

  const buildText = (partial = ''): string => {
    const baseline = baselineRef.current;
    const finals = finalsRef.current.trim();
    const p = partial.trim();
    const tail = [finals, p].filter(Boolean).join(' ');
    if (!tail) return baseline;
    if (!baseline) return tail;
    const needsSpace = !/\s$/.test(baseline);
    return needsSpace ? `${baseline} ${tail}` : `${baseline}${tail}`;
  };

  const stop = useCallback(() => {
    try { sttRef.current?.stop(); } catch { /* noop */ }
    sttRef.current = null;
    setIsRecording(false);
    // Utrwal: baseline + wszystkie finale (partial odrzucamy)
    try { onChangeRef.current(buildText()); } catch { /* noop */ }
    baselineRef.current = '';
    finalsRef.current = '';
  }, []);

  const start = useCallback(() => {
    if (disabled) return;
    if (!isSupported) {
      setError('Rozpoznawanie mowy nie jest wspierane w tej przeglądarce. Użyj najnowszego Chrome.');
      return;
    }
    if (sttRef.current) return;
    setError(null);
    baselineRef.current = valueRef.current || '';
    finalsRef.current = '';
    const deviceId = (() => {
      try { return localStorage.getItem(deviceIdStorageKey) || undefined; } catch { return undefined; }
    })();
    sttRef.current = startStt(
      lang,
      {
        onStart: () => setIsRecording(true),
        onEnd: () => setIsRecording(false),
        onPartial: (text) => {
          try { onChangeRef.current(buildText(text)); } catch { /* noop */ }
        },
        onFinal: (text) => {
          const t = String(text || '').trim();
          if (t) finalsRef.current = finalsRef.current ? `${finalsRef.current} ${t}` : t;
          try { onChangeRef.current(buildText()); } catch { /* noop */ }
        },
        onError: (err) => {
          const msg = String(err?.message || '');
          // no-speech / aborted są naturalne przy auto-restart — nie pokazuj
          if (/no-speech|aborted/i.test(msg)) return;
          setError(msg || 'Błąd nagrywania');
        },
      },
      { continuous: true, interimResults: true, prewarmDeviceId: deviceId },
    );
  }, [disabled, isSupported, lang, deviceIdStorageKey]);

  const toggle = useCallback(() => {
    if (sttRef.current) stop(); else start();
  }, [start, stop]);

  useEffect(() => {
    return () => {
      try { sttRef.current?.stop(); } catch { /* noop */ }
      sttRef.current = null;
    };
  }, []);

  return { isSupported, isRecording, error, start, stop, toggle };
}
